import React from 'react'
import {
  BlockStack,
  Box,
  Button,
  Card,
  Icon,
  InlineStack,
  Spinner,
  Text,
  type IconSource,
} from '@/components/shopify-web-components'
import { ImageIcon } from '@shopify/polaris-icons'

type CardImageUploadProps = {
  label: string
  displayImageUrl?: string
  hasImage: boolean
  disabled: boolean
  isUploading: boolean
  hiddenInput: React.ReactNode
  onButtonClick: () => void
}

export function CardImageUpload({
  label,
  displayImageUrl,
  hasImage,
  disabled,
  isUploading,
  hiddenInput,
  onButtonClick,
}: CardImageUploadProps) {
  return (
    <BlockStack gap="300">
      <Text as="h3" variant="headingSm">
        {label}
      </Text>

      <Card>
        <Box
          padding="100"
          borderRadius="200"
          background="bg-surface"
          borderColor="border"
          borderWidth="0"
        >
          <BlockStack gap="300" align="center">
            {hasImage
              ? (
                  <BlockStack gap="300" align="center" inlineAlign="stretch">
                    <Box
                      background="bg-surface-secondary"
                      borderRadius="200"
                      padding="200"
                      width="100%"
                      position="relative"
                    >
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          justifyContent: 'center',
                          width: '100%',
                        }}
                      >
                        <img
                          src={displayImageUrl}
                          alt={label}
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '150px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            opacity: isUploading ? 0.5 : 1,
                            display: 'block',
                          }}
                        />
                        {isUploading && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 10,
                            }}
                          >
                            <Spinner size="small" />
                          </div>
                        )}
                      </div>
                    </Box>
                    <InlineStack gap="200" align="center">
                      <Button
                        size="slim"
                        onClick={onButtonClick}
                        disabled={disabled || isUploading}
                      >
                        {isUploading ? 'Uploading...' : 'Replace Image'}
                      </Button>
                    </InlineStack>
                  </BlockStack>
                )
              : (
                  <BlockStack gap="300" align="center">
                    {isUploading
                      ? (
                          <BlockStack gap="200" align="center" inlineAlign="center">
                            <Spinner size="large" />
                            <Text as="p" variant="bodyMd" alignment="center">
                              Uploading image...
                            </Text>
                          </BlockStack>
                        )
                      : (
                          <BlockStack gap="400" align="center">
                            <Icon source={ImageIcon as IconSource} />
                            <Button
                              onClick={onButtonClick}
                              disabled={disabled || isUploading}
                              loading={isUploading}
                              size="slim"
                            >
                              {isUploading ? 'Uploading...' : 'Browse files'}
                            </Button>
                          </BlockStack>
                        )}
                  </BlockStack>
                )}
          </BlockStack>
        </Box>
      </Card>

      {hiddenInput}
    </BlockStack>
  )
}
